// ONE implementation, two runtimes — the same function object the edge
// functions use, so the app and the server can never disagree about which
// vendors are retired. (Same pattern as canonicalItemCost.mjs; do not add a
// browser copy.) A retired vendor's product may still describe stock on the
// shelf — the count-presentation resolver deliberately never calls this —
// but it must not enter any NEW-order path.
import { vendorIsRetired } from '../../../supabase/functions/_shared/vendorState.mjs';
export {
  vendorIsRetired,
  RETIRED_VENDOR_STATUSES,
} from '../../../supabase/functions/_shared/vendorState.mjs';
import { hasPackageBasisConflict } from '../../../supabase/functions/_shared/packageBasis.mjs';

const toFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Price-source tiers, best → worst. Lower index = more authoritative.
 *
 *   verified invoice/receipt
 *   confirmed received purchase / PO (e.g. verified shopping trip)
 *   approved manual price
 *   accepted vendor price / order guide (also: unverified invoice)
 *   legacy item price (legacy_backfill rows)
 *   missing / unpriceable
 */
export const PRICE_SOURCE_PRIORITY = Object.freeze([
  'verified_invoice_receipt',
  'confirmed_received_purchase',
  'approved_manual_price',
  'accepted_vendor_price',
  'legacy_item_price',
  'missing',
]);

const rankOf = (tier) => PRICE_SOURCE_PRIORITY.indexOf(tier);

/**
 * Ranks a price source against PRICE_SOURCE_PRIORITY.
 *
 * @param {string|null} sourceType         - e.g. 'invoice', 'receipt', 'order_guide',
 *                                           'manual', 'shopping_trip', 'pos', 'legacy_backfill'
 * @param {string|null} verificationStatus - 'verified' | 'unverified' | 'rejected'
 * @returns {number} Lower = better. Unknown/rejected sources rank as 'missing'.
 */
export function rankPriceSource(sourceType, verificationStatus) {
  if (!sourceType || verificationStatus === 'rejected') return rankOf('missing');
  const verified = verificationStatus === 'verified';

  switch (sourceType) {
    case 'invoice':
    case 'receipt':
      return verified ? rankOf('verified_invoice_receipt') : rankOf('accepted_vendor_price');
    case 'shopping_trip':
      return verified ? rankOf('confirmed_received_purchase') : rankOf('accepted_vendor_price');
    case 'manual':
      return verified ? rankOf('approved_manual_price') : rankOf('accepted_vendor_price');
    case 'order_guide':
    case 'pos':
      return rankOf('accepted_vendor_price');
    case 'legacy_backfill':
      return rankOf('legacy_item_price');
    default:
      return rankOf('missing');
  }
}

/**
 * Whether the option actually provides a price: a positive package price or a
 * positive normalized cost. An option without one ("no price provided") stays
 * visible in relationship views as a nudge to add the price, but is never
 * offered for buying — a missing price must not behave as $0.
 */
export function hasProvidedPrice(option) {
  const price = toFiniteNumber(option?.package_price);
  if (price !== null && price > 0) return true;
  const cost = toFiniteNumber(option?.normalized_unit_cost);
  return cost !== null && cost > 0;
}

const isAllocatable = (option) =>
  !!option &&
  option.status === 'active' &&
  !!option.is_approved &&
  !option.is_hidden_from_allocation &&
  !vendorIsRetired(option) &&
  hasProvidedPrice(option) &&
  // An option whose stored facts describe DIFFERENT packages (qty_per_package
  // vs inner-pack structure vs the deterministic comparison) has no single
  // orderable package to allocate. It needs repair, not a purchase order.
  !hasPackageBasisConflict(option);

const timeOf = (value) => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const costOf = (option) => {
  // Unrounded quotient first: this ordering decides the fallback vendor
  // selection when the relationship comparison is unresolved, and a tie
  // manufactured by the 6dp rounding of the stored column must never pick
  // the wrong "cheapest" vendor. The stored cost is only a last resort for
  // rows missing either input.
  const price = toFiniteNumber(option?.package_price);
  const qty = toFiniteNumber(option?.qty_per_package);
  if (price !== null && price > 0 && qty !== null && qty > 0) return price / qty;
  const cost = toFiniteNumber(option?.normalized_unit_cost);
  return cost === null ? Number.POSITIVE_INFINITY : cost;
};

/**
 * Picks the single authoritative purchase option: the best-ranked active,
 * approved, allocation-visible option. Ties within a source tier break by
 * newest effective_date first, then cheapest normalized_unit_cost.
 *
 * @param {Array} options
 * @returns {Object|null} null when no option qualifies.
 */
export function pickAuthoritativeOption(options) {
  const candidates = (options || []).filter(isAllocatable);
  if (!candidates.length) return null;

  return [...candidates].sort((a, b) => {
    const rankDiff =
      rankPriceSource(a.source_type, a.verification_status) -
      rankPriceSource(b.source_type, b.verification_status);
    if (rankDiff !== 0) return rankDiff;
    const dateDiff = timeOf(b.effective_date) - timeOf(a.effective_date);
    if (dateDiff !== 0) return dateDiff;
    return costOf(a) - costOf(b);
  })[0];
}

/**
 * Lists all allocation-eligible options (active, approved, not hidden),
 * sorted preferred-first, then cheapest normalized_unit_cost.
 *
 * @param {Array} options
 * @returns {Array}
 */
export function listAllocatableOptions(options) {
  return (options || [])
    .filter(isAllocatable)
    .sort((a, b) => {
      const prefDiff = (b.is_preferred ? 1 : 0) - (a.is_preferred ? 1 : 0);
      if (prefDiff !== 0) return prefDiff;
      return costOf(a) - costOf(b);
    });
}

/**
 * Normalized per-count-unit cost from a package price and pack size,
 * rounded to 6 decimal places.
 *
 * @returns {number|null} null when either input is missing or non-positive.
 */
export function computeNormalizedUnitCost({ package_price, qty_per_package } = {}) {
  const price = toFiniteNumber(package_price);
  const qty = toFiniteNumber(qty_per_package);
  if (price === null || qty === null || price <= 0 || qty <= 0) return null;
  return Math.round((price / qty) * 1e6) / 1e6;
}

/**
 * Guard against garbage prices: must be positive, finite, and under 1e6.
 */
export function isPricePlausible(cost) {
  const parsed = toFiniteNumber(cost);
  return parsed !== null && parsed > 0 && parsed < 1e6;
}

/**
 * Decides whether a candidate price should overwrite the current verified
 * cost for an item. Accepts only when the candidate is matched to the
 * expected item, has a valid conversion, a positive and plausible price,
 * comes from a verified source, and is newer than the current price.
 *
 * @param {{ candidate: Object, current: Object }} args
 * @returns {{ accept: boolean, reason: string }}
 */
export function shouldUpdateVerifiedCost({ candidate = {}, current = {} } = {}) {
  if (!candidate.matchedItemId || candidate.matchedItemId !== candidate.expectedItemId) {
    return { accept: false, reason: 'not_matched' };
  }
  if (candidate.conversionStatus !== 'valid') {
    return { accept: false, reason: 'unresolved_conversion' };
  }
  const price = toFiniteNumber(candidate.packagePrice);
  if (price === null || price <= 0) {
    return { accept: false, reason: 'non_positive_price' };
  }
  const cost = candidate.normalizedCost ?? candidate.packagePrice;
  if (!isPricePlausible(cost)) {
    return { accept: false, reason: 'implausible_price' };
  }
  if (candidate.verificationStatus !== 'verified') {
    return { accept: false, reason: 'unverified_source' };
  }
  const candidateTime = Date.parse(candidate.effectiveDate);
  const currentTime = Date.parse(current.effectiveDate);
  if (
    Number.isFinite(currentTime) &&
    (!Number.isFinite(candidateTime) || candidateTime <= currentTime)
  ) {
    return { accept: false, reason: 'stale_effective_date' };
  }
  return { accept: true, reason: 'accepted' };
}

/**
 * One-line count of active options, e.g. '3 active purchase options'.
 */
export function optionSummaryLine(options) {
  const count = (options || []).filter((o) => o?.status === 'active').length;
  return `${count} active purchase option${count === 1 ? '' : 's'}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (value) => typeof value === 'string' && UUID_RE.test(value.trim());

/**
 * Compact human label, e.g. 'Sysco · 4 × 1 gal case · $4.60/gal'.
 * Missing fields are skipped gracefully.
 */
export function formatOptionLabel(option) {
  if (!option) return '';
  const parts = [];

  // vendor_name_text is the raw name read off the source document and is the
  // only vendor word an unresolved option has — an import that ran before its
  // vendor rows existed leaves vendor_id NULL and nothing else to print.
  // vendor_description is deliberately NOT here: it is the vendor's PRODUCT
  // description ("Brioche Hamburger Buns, Sliced"), so using it as the vendor
  // slot labels every option with its own item name and hides the vendor.
  // vendor_id is a last resort and only when it reads as a name: once options
  // are linked to vendors that column holds a UUID, and "3f2a…" tells a buyer
  // less than nothing.
  const vendor = option.vendor_name
    || option.vendor?.display_name
    || option.vendor_name_text
    || (isUuid(option.vendor_id) ? null : option.vendor_id);
  if (vendor) parts.push(String(vendor));

  const qty = toFiniteNumber(option.qty_per_package);
  const pack =
    option.pack_description ||
    (qty !== null && option.purchase_unit ? `${qty} × ${option.purchase_unit}` : option.purchase_unit);
  if (pack) parts.push(String(pack));

  const cost = toFiniteNumber(option.normalized_unit_cost);
  if (cost !== null && cost > 0) {
    parts.push(option.count_unit ? `$${cost.toFixed(2)}/${option.count_unit}` : `$${cost.toFixed(2)}`);
  } else if (parts.length > 0 && !hasProvidedPrice(option)) {
    parts.push('No price provided');
  }

  return parts.join(' · ');
}
